import { BusinessModel } from '@/http/models/business'
import { BusinessResponses } from '@/types'
import React, { createContext, useContext, useEffect, useState } from 'react'

interface BusinessContextType {
    profile: BusinessResponses.BusinessProfile['result'] | null
    loading: boolean
    currency: string
    currencySymbol: string
    formatCurrency: (amount: number) => string
    refresh: () => Promise<void>
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined)

export const BusinessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [profile, setProfile] = useState<BusinessResponses.BusinessProfile['result'] | null>(null)
    const [loading, setLoading] = useState(true)

    const fetchProfile = async () => {
        try {
            const response = await BusinessModel.getBusinessProfile()
            if (response.success && response.data) {
                setProfile(response.data.result)
            }
        } catch (e) {
            console.error('Failed to fetch business profile', e)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchProfile()
    }, [])

    const currency = profile?.app_settings?.currency?.toUpperCase() || 'TK'

    const currencySymbol = currency === 'USD' ? '$' : 'TK'

    const formatCurrency = (amount: number) => {
        const formatted = amount.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        })
        return `${currencySymbol} ${formatted}`
    }

    return (
        <BusinessContext.Provider
            value={{
                profile,
                loading,
                currency,
                currencySymbol,
                formatCurrency,
                refresh: fetchProfile
            }}
        >
            {children}
        </BusinessContext.Provider>
    )
}

export const useBusiness = () => {
    const context = useContext(BusinessContext)
    if (context === undefined) {
        throw new Error('useBusiness must be used within a BusinessProvider')
    }
    return context
}
